/**
 * VideoGallery - Browse and manage generated videos
 * Features: Thumbnails, playback, download, prompt display
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Video, Play, Download, Clock, Calendar, X, 
  Loader2, Film, Sparkles, ArrowLeft, Trash2,
  Eye, FileVideo, Zap
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoGallery() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [filter, setFilter] = useState('all'); // all, completed, processing, failed

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth?redirect=/video-gallery');
      return;
    }
    fetchVideos();
  }, [user, token]);

  const fetchVideos = async () => {
    try {
      // Fetch from both video_jobs and media_generations
      const [jobsRes, mediaRes] = await Promise.all([
        axios.get(`${API}/video/my-jobs`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/user/media-history`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { media: [] } }))
      ]);

      // Combine and dedupe by id
      const jobs = jobsRes.data.jobs || [];
      const mediaVideos = (mediaRes.data.media || []).filter(m => m.type === 'video');
      
      // Merge - prefer jobs data (has status info), add media data for video_data
      const combined = jobs.map(job => {
        const media = mediaVideos.find(m => m.id === job.id);
        return {
          ...job,
          video_data: media?.video_data || job.video_data,
          thumbnail: media?.thumbnail || job.thumbnail
        };
      });

      // Add any media videos not in jobs
      mediaVideos.forEach(media => {
        if (!combined.find(c => c.id === media.id)) {
          combined.push({
            ...media,
            status: 'completed',
            progress: 100
          });
        }
      });

      // Sort by date (newest first)
      combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setVideos(combined);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (video) => {
    if (!video.video_data) return;
    
    const link = document.createElement('a');
    link.href = `data:video/mp4;base64,${video.video_data}`;
    link.download = `tripletc-video-${video.id.slice(0, 8)}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredVideos = videos.filter(v => {
    if (filter === 'all') return true;
    return v.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400 bg-green-500/20';
      case 'processing': return 'text-yellow-400 bg-yellow-500/20';
      case 'queued': return 'text-blue-400 bg-blue-500/20';
      case 'failed': return 'text-red-400 bg-red-500/20';
      default: return 'text-zinc-400 bg-zinc-500/20';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button 
            onClick={() => navigate('/templates')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Templates
          </button>
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Film className="w-6 h-6 text-white" />
                </div>
                Video Gallery
              </h1>
              <p className="text-zinc-400 mt-2">
                {videos.length} video{videos.length !== 1 ? 's' : ''} generated with Sora 2
              </p>
            </div>
            
            <Button
              onClick={() => navigate('/templates')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate New Video
            </Button>
          </div>
        </motion.div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="video-filters">
          {['all', 'completed', 'processing', 'failed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                filter === f 
                  ? 'bg-teal-500 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f !== 'all' && (
                <span className="ml-2 text-xs opacity-70">
                  ({videos.filter(v => v.status === f).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Video Grid */}
        {filteredVideos.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <FileVideo className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-zinc-400 mb-2">No videos yet</h3>
            <p className="text-zinc-500 mb-6">Generate your first AI video with Sora 2</p>
            <Button
              onClick={() => navigate('/templates')}
              className="bg-gradient-to-r from-purple-500 to-pink-500"
            >
              <Zap className="w-4 h-4 mr-2" />
              Create Video
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="video-grid">
            {filteredVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-all group"
                data-testid={`video-card-${video.id}`}
              >
                {/* Thumbnail / Preview */}
                <div 
                  className="relative aspect-video bg-zinc-800 cursor-pointer"
                  onClick={() => video.status === 'completed' && setSelectedVideo(video)}
                >
                  {video.thumbnail ? (
                    <img 
                      src={`data:image/jpeg;base64,${video.thumbnail}`}
                      alt="Video thumbnail"
                      className="w-full h-full object-cover"
                    />
                  ) : video.status === 'completed' ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                      <Video className="w-12 h-12 text-zinc-500" />
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      {video.status === 'processing' || video.status === 'queued' ? (
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 text-teal-400 animate-spin mx-auto mb-2" />
                          <span className="text-sm text-zinc-400">{video.progress || 0}%</span>
                        </div>
                      ) : (
                        <X className="w-12 h-12 text-red-500" />
                      )}
                    </div>
                  )}
                  
                  {/* Play overlay */}
                  {video.status === 'completed' && video.video_data && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-white ml-1" />
                      </div>
                    </div>
                  )}
                  
                  {/* Status badge */}
                  <div className={`absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(video.status)}`}>
                    {video.status}
                  </div>
                  
                  {/* Duration badge */}
                  {video.duration && (
                    <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 rounded text-xs text-white flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {video.duration}s
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <p className="text-zinc-300 text-sm line-clamp-2 mb-3">
                    {video.prompt || 'No prompt available'}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-xs text-zinc-500">
                      <Calendar className="w-3 h-3" />
                      {formatDate(video.created_at)}
                    </div>
                    
                    <div className="flex gap-2">
                      {video.status === 'completed' && video.video_data && (
                        <>
                          <button
                            onClick={() => setSelectedVideo(video)}
                            className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
                            title="Watch"
                          >
                            <Eye className="w-4 h-4 text-zinc-400" />
                          </button>
                          <button
                            onClick={() => handleDownload(video)}
                            className="p-2 bg-zinc-800 hover:bg-teal-600 rounded-lg transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4 text-zinc-400" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Video Player Modal */}
        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedVideo(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-zinc-900 rounded-2xl overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                  <h3 className="text-lg font-semibold text-white">Video Player</h3>
                  <button
                    onClick={() => setSelectedVideo(null)}
                    className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>

                {/* Video */}
                <div className="flex-1 bg-black flex items-center justify-center">
                  {selectedVideo.video_data ? (
                    <video
                      src={`data:video/mp4;base64,${selectedVideo.video_data}`}
                      controls
                      autoPlay
                      className="max-w-full max-h-[60vh]"
                      data-testid="video-player"
                    />
                  ) : (
                    <div className="text-zinc-500">Video not available</div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="p-4 border-t border-zinc-800">
                  <p className="text-zinc-300 text-sm mb-4">{selectedVideo.prompt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {selectedVideo.duration}s
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(selectedVideo.created_at)}
                      </span>
                    </div>
                    <Button
                      onClick={() => handleDownload(selectedVideo)}
                      className="bg-teal-500 hover:bg-teal-600"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download MP4
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
